/**
 * Fias Forge arche — client-side types (ADR-023).
 *
 * Wire shapes come from `@/lib/api` (mirrors of the server's serializers);
 * everything here is page state that never crosses the network.
 */
export type {
  ForgeSummary,
  SharedForgeSummary,
  ForgeStatus,
  ForgeStopReason,
  ForgeModelTier,
  ForgeSize,
  ForgeTurnSummary,
  ForgeTestBuildSummary,
  ForgeTestChannelInfo,
  ForgeUsage,
  ForgeOpenQuestion,
} from '@/lib/api';

import type { ForgeOpenQuestion } from '@/lib/api';

/** The question Claude is waiting on (AskUserQuestion), with the turn to answer it on. */
export interface ForgePendingQuestion {
  turnId: string;
  questionId: string;
  questions: Extract<ForgeOpenQuestion, { kind: 'questions' }>['questions'];
}

/** The plan Claude is waiting for the owner to approve (ExitPlanMode, ADR-023 A14). */
export interface ForgePendingPlan {
  turnId: string;
  questionId: string;
  /** Markdown. */
  plan: string;
}

/** A chat bubble in the forge conversation pane. */
export interface ForgeMessage {
  role: 'user' | 'assistant';
  content: string;
  /** ISO timestamp; also the key that binds tool events to the bubble. */
  timestamp: string;
}

/** A screenshot the runtime pushed during a turn (`forge_screenshot`). */
export interface ForgeScreenshot {
  /** Unique for the page session (paths repeat when Claude re-uses a name). */
  id: string;
  /** Workspace-relative path the runtime wrote the image to. */
  path: string;
  /** `data:image/<type>;base64,…` — ready for an `<img src>`. */
  dataUrl: string;
  requestId: string;
  receivedAt: string;
}

/** A tool call / result inside a turn, shaped for the shared VerboseTimeline. */
export interface ForgeToolEvent {
  type: 'tool_call' | 'tool_result';
  timestamp: string;
  data: {
    toolName?: string;
    toolInput?: unknown;
    toolResult?: unknown;
    isError?: boolean;
    /** Pairs a result with its call (the compact tool view shows them as one row). */
    toolUseId?: string;
  };
}

/** Forge statuses in which the runtime is (or is becoming) alive. */
export const FORGE_ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'provisioning',
  'running',
  'stopping',
]);

/** Statuses the page polls through: the platform will move them without user input. */
export const FORGE_TRANSITIONAL_STATUSES: ReadonlySet<string> = new Set([
  'provisioning',
  'stopping',
]);
